import styled from 'styled-components';
import { Card, Row, Button, Transfer } from 'antd';
import { IThemeProps, IToken, CustomAliasToken } from 'store/ducks/globalTypes';
import { IThemeSelectorProps } from 'store/ducks/Global/types';

/* ---------------- COMPONENT ---------------- */

export const Container = styled.div`
  padding: 24px;
  min-height: calc(100vh - 200px);

  .ant-select.ant-select-in-form-item {
    width: auto;
  }
`;

export const HorizontalLayout = styled('div')<IThemeSelectorProps>`
  padding: 0 24px 24px;
`;

export const GridContainer = styled('div')<{ token: any }>`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
  justify-items: center;
  width: 100%;

  > a,
  > div {
    width: 100%;
    max-width: 300px;
  }

  .ant-card {
    width: 100%;
  }

  @media (max-width: 900px) {
    grid-template-columns: repeat(3, 1fr);
    gap: 20px;
  }

  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
  }

  @media (max-width: 400px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

export const RelativeContainer = styled('div')`
  position: relative;
  width: 100%;
  cursor: pointer;
`;

export const ContentWrapper = styled('div')`
  width: 100%;
  padding: 24px;

  @media (max-width: 768px) {
    padding: 12px;
  }
`;

export const TransferCard = styled(Card)<IThemeProps>`
  background: #fff;
  border: 1px solid #52c41a;
  padding: 12px;

  .ant-card-head-title {
    color: ${(props) =>
      props.scheme === 'greenModeTheme' ? 'inherit' : '#000 !important'};
  }

  .ant-transfer {
    display: flex;
    justify-content: center;
    width: 100%;
  }

  .ant-transfer-list {
    width: 100% !important;
    max-width: 400px;
  }

  .ant-select {
    margin-bottom: 12px;
  }

  @media (max-width: 768px) {
    .ant-transfer-list {
      max-width: 100%;
    }
  }
`;

/* ---------------- TYPOGRAPHY ---------------- */

export const Title = styled.h1<{ scheme?: string }>`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 12px;
  color: ${(props) => (props.scheme === 'darkUITheme' ? '#fff' : '#111827')};
`;

export const Description = styled.p<{ scheme?: string }>`
  font-size: 14px;
  margin-bottom: 20px;
  color: ${(props) => (props.scheme === 'darkUITheme' ? '#d1d5db' : '#6b7280')};
`;

/* ---------------- FORM LAYOUT ---------------- */

export const StyledRow = styled(Row)`
  margin-top: 1rem;
`;

export const FormRow = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

/* ---------------- BUTTON ---------------- */

export const ApplyButton = styled(Button)<
  IThemeProps &
    Partial<IToken> & {
      isClientReqPage?: boolean;
      hasDisplayedData?: boolean;
      isCompliancePage?: boolean;
      isComplianceCreationPage?: boolean;
    }
>`
  border-radius: 6px;
  font-weight: 500;

  ${(props) =>
    props.scheme === 'deepNavyTheme' &&
    !props.disabled &&
    props.children === 'Save' &&
    `
    && {
      filter: brightness(1.8) !important;
    }
  `}

  background-color: ${(props) => {
    if (props.type === 'primary') return '#ff7875';
    const t = props.token?.colorPrimary;
    if (props.scheme === 'darkUITheme' && props.children === 'Copy All')
      return t ?? 'inherit';
    return 'inherit';
  }};
  border-color: ${(props) => {
    if (props.type === 'primary') return '#ff7875';
    const t = props.token?.colorPrimary;
    if (props.scheme === 'darkUITheme' && props.children === 'Copy All')
      return t ?? 'inherit';
    return 'inherit';
  }};
  color: ${(props) => {
    if (props.type === 'primary') return '#fff !important';
    if (props.children === 'Copy All') return '#fff !important';
    const isNavy = props.scheme === 'deepNavyTheme';
    if (
      isNavy &&
      (props.children === 'Merge selected' ||
        props.children === 'Copy selected' ||
        props.children === 'Save')
    )
      return '#000000 !important';
    return 'inherit';
  }};

  @media (max-width: 1500px) {
    width: 100px !important;
    font-size: ${(props) =>
      props.isClientReqPage && props.hasDisplayedData ? '10px' : '12px'};
    padding: 4px 8px;
    margin-left: ${(props) => {
      if (props.type === 'primary' && props.children === 'Display') {
        if (
          !props.hasDisplayedData &&
          (props.isCompliancePage || props.isComplianceCreationPage)
        )
          return '150px';
        return '-20px';
      }
      return '0';
    }};
  }

  &:hover {
    background-color: ${(props) => {
      if (props.type === 'primary') return '#ff9c99';
      const t = props.token?.colorPrimary;
      const hover = props.token?.colorPrimaryBgHover;
      if (props.scheme === 'darkUITheme') return t ?? 'inherit';
      return hover ?? 'inherit';
    }};
    border-color: ${(props) => {
      if (props.type === 'primary') return '#ff9c99';
      const t = props.token?.colorPrimary;
      if (props.scheme === 'darkUITheme') return t ?? 'inherit';
      return 'inherit';
    }};
    color: ${(props) => {
      if (props.type === 'primary') return '#fff !important';
      if (props.scheme === 'darkUITheme') return '#fff !important';
      if (
        props.scheme === 'deepNavyTheme' &&
        (props.children === 'Merge selected' ||
          props.children === 'Copy selected' ||
          props.children === 'Save')
      )
        return '#fff !important';
      return 'inherit';
    }};
  }
`;

export const SaveButtonDiv = styled('div')`
  display: flex;
  height: -webkit-fill-available;
  align-items: center;
  justify-content: end;
`;

export const ActionButtons = styled(Card)`
  .ant-card-body {
    display: flex;
    gap: 20px;
  }
`;

export const ContextCard = styled(Card)`
  margin-top: 12px;
  border-radius: 10px;
  border: 1px solid #f0f0f0;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.04);
  width: max-content;
  margin: auto;

  .ant-card-head {
    font-size: 13px;
    font-weight: 600;
    padding: 8px 12px;
    min-height: 36px;
  }

  .ant-card-body {
    padding: 10px 12px;
    width: max-content;
  }
`;

export const ContextRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  font-size: 12px;
  color: #374151;

  b {
    font-size: 12px;
    color: #111827;
    font-weight: 600;
  }

  &:not(:last-child) {
    border-bottom: 1px solid #f3f4f6;
  }

  span {
    padding: 0 10px 0 0;
  }
`;

export const EmptyState = styled.div`
  font-size: 13px;
  text-align: center;
  padding: 12px 0;
`;

export const ContextRowFlex = styled.div`
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  align-items: center;
  padding-left: 24px;
`;

export const ContextCell = styled.div`
  display: flex;
  flex-direction: column;

  span {
    font-size: 12px;
    opacity: 0.6;
  }

  b {
    font-size: 14px;
    font-weight: 600;
  }
`;

export const EqualTransfer = styled(Transfer)`
  width: 100%;

  .ant-transfer {
    display: flex;
    width: 100%;
  }

  .ant-transfer-list {
    flex: 1 1 0 !important;
    width: 0 !important;
  }

  .ant-transfer-list-body {
    height: 100%;
  }

  .ant-transfer-list-content-item {
    height: 32px;
    display: flex;
    align-items: center;
  }

  .transfer-item {
    width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

// export const SelectedContextBox = styled.div<{
//   scheme: string;
//   token?: CustomAliasToken;
// }>`
//   display: flex;
//   flex-wrap: wrap;
//   gap: 15px;
//   margin-bottom: 16px;
//   padding: 16px;
//   border-radius: 8px;
// `;

// export const ContextItem = styled.div`
//   padding: 4px 10px;
//   border-radius: 5px;
//   background: rgba(0, 0, 0, 0.04);
//   font-size: 12px;
//   line-height: 18px;
//   max-width: 100%;
//   word-break: break-word;
// `;

// export const SelectedFiltersSection = styled.div<{
//   scheme?: string;
//   token?: CustomAliasToken;
// }>`
//   background-color: ${({ token }) => token?.colorFillContent};
//   border-radius: 8px;
//   padding: 16px;
//   margin-bottom: 24px;
//   border: 1px solid ${({ token }) => token?.colorBorderSecondary};
// `;

// export const SelectedFiltersTitle = styled.h3<{ token?: CustomAliasToken }>`
//   font-size: 14px;
//   font-weight: 600;
//   margin: 0 0 12px 0;
//   color: ${({ token }) => token?.colorText || '#111827'};
// `;

export const SelectedFiltersSection = styled.div<{
  scheme?: string;
  token?: CustomAliasToken;
}>`
  position: relative;
  overflow: hidden;
  padding: 22px 24px;
  margin-bottom: 24px;
  border: 1px solid var(--filter-border);
  border-radius: 16px;
  background: ${({ token }) => {
    const baseColor = token?.colorBgContainer ?? '#ffffff';
    const secondaryColor = token?.colorFillContent ?? '#f5f5f5';

    return `linear-gradient(135deg, ${baseColor} -135%, ${secondaryColor} 135%)`;
  }};
  box-shadow: 0 0 24px
    ${({ scheme }) =>
      scheme === 'darkUITheme'
        ? 'rgba(0, 0, 0, 0.18)'
        : 'rgba(71, 85, 150, 1)'};

  transition: box-shadow 0.25s ease, border-color 0.25s ease;

  &::before {
    content: '';
    position: absolute;
    inset: 0 auto 0 0;
    width: 4px;
    background: linear-gradient(180deg, var(--filter-accent), #8b5cf6);
  }

  &:hover {
    box-shadow: 0 8px 28px
      ${({ scheme }) =>
        scheme === 'darkUITheme'
          ? 'rgba(0, 0, 0, 0.25)'
          : 'rgba(71, 85, 150, 0.13)'};
  }

  @media (max-width: 768px) {
    padding: 18px 16px;
    border-radius: 12px;
  }
`;

export const SelectedFiltersHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
`;

export const SelectedFiltersHeading = styled.div`
  display: flex;
  align-items: center;
  gap: 13px;
  min-width: 0;
`;

export const SelectedFiltersTitle = styled.p<{
  token?: CustomAliasToken;
}>`
  margin: 0;
  color: ${({ token }) => token?.colorText ?? '#111827'} !important;
  font-size: 16px;
  font-weight: 700;
  letter-spacing: -0.35px;
  line-height: 1.5;
`;

export const SelectedContextBox = styled.div<{
  scheme?: string;
}>`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 1100px) {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
  }

  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`;

export const ContextItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 7px;
  min-width: 0;
  padding: 4px 6px;
  border: 1px solid rgba(148, 163, 184, 0.19);
  border-radius: 11px;
  background: #fff;
  transition: background 0.2s ease, border-color 0.2s ease, transform 0.2s ease;

  &:hover {
    background: rgba(99, 102, 241, 0.055);
    border-color: rgba(99, 102, 241, 0.3);
    transform: translateY(-2px);
  }

  strong {
    color: #8490a3;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.8px;
    line-height: 1.4;
    text-transform: uppercase;
  }

  span {
    max-width: 100%;
    color: ${({ theme }) => theme?.colors?.text ?? 'inherit'};
    font-size: 13px;
    font-weight: 600;
    line-height: 1.6;
    overflow-wrap: anywhere;
  }
`;
